import React, { useCallback, useContext, useEffect } from 'react';
import {
  Input,
  InputLabel,
  FormControl,
  FormHelperText,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import { InputProps } from '@material-ui/core/Input';
import { useFormContext, Controller, FieldError } from 'react-hook-form';

import { InputLabelProps } from '@material-ui/core/InputLabel';
import { InputBaseComponentProps } from '@material-ui/core/InputBase';
import { get } from 'lodash';
import { useId } from '../../hooks';
import { ClassApi } from '../../types/styles';
import { ItemFormContext } from '../item-form/ReactItemFormContext';
import { CustomActionCell } from '../index';
import { QuestionCircleIcon } from '../icon/solid';

const useStyles = makeStyles(({ palette, colors }: Theme) => createStyles({
  root: {},
  input: {},
  labelRoot: {
    '&$focused': {
      color: palette.text.secondary,
    },
  },
  inputUnderline: {
    '&:before': {
      borderBottomColor: palette.input.borderBottomColor,
    },
    '&&:hover:before': {
      borderBottomColor: palette.input.borderBottomColor,
    },
    '&:after': {
      borderBottomColor: palette.menuAction.selected,
    },
    '&$focused:after': {
      borderBottomColor: palette.menuAction.selected,
    },
    '&$disabled:before': {
      borderBottomStyle: 'dashed',
    },
  },
  focused: {},
  hovered: {},
  disabled: {
    color: palette.disabledInput.color,
  },
  iconTooltip: {
    fontSize: 14,
    color: colors.icon,
    verticalAlign: 'middle',
    marginBottom: 4,
    marginLeft: 8,
  },
  commentButtonWrapper: {
    textAlign: 'center',
  },
  comment: {
    fontSize: '12px',
    padding: '0 10px 0 10px',
  },
}), { name: 'FormInput' });


type Props = Omit<InputProps, 'classes'> & {
  label?: string;
  name: string;
  submitOnBlur?: boolean;
  hideErrors?: boolean;
  type?: 'text' | 'password' | 'textarea' | 'number';
  classes?: ClassApi<typeof useStyles>;
  inputLabelProps?: Omit<InputLabelProps, 'classes'>;
  tooltip?: string;
  getValueAfterInit?: (() => string);
};

const defaultInputProps: Partial<InputBaseComponentProps> = {
  spellCheck: false,
};

const FormInput = ({
  name,
  label,
  disabled = false,
  hideErrors = false,
  submitOnBlur = false,
  className,
  type = 'text',
  endAdornment,
  classes: externalClasses = {},
  inputLabelProps: labelProps = {},
  inputProps = {},
  tooltip = '',
  getValueAfterInit,
  ...muiInputProps
}: Props) => {
  const classes = useStyles({ classes: externalClasses });
  const {
    errors, formState, control, watch, setValue, getValues,
  } = useFormContext();
  const { isDirty, touched: allTouches } = formState;
  const { submit } = useContext(ItemFormContext);
  const inputId = useId('form-input-');
  const errorId = useId('input-error-');
  const value = watch(name);

  const extras: Partial<InputProps> = type === 'textarea' ? {
    multiline: true,
    rowsMax: 10,
  } : {
    type,
  };

  const submitForm = useCallback(() => {
    if (value !== '' && isDirty) {
      submit();
    }
  }, [value, submit, isDirty]);

  const error = (get(errors, name) as FieldError)?.message;
  const touched = (get(allTouches, name));

  const onChangeHandler = useCallback((e) => {
    let val = e.target.value;
    if (type === 'number' && inputProps) {
      const { min, max } = inputProps;
      val = (min && min > val) ? min : val;
      val = (max && max < val) ? max : val;
    }
    setValue(name, val, { shouldValidate: true, shouldDirty: true });
  }, [setValue, name, type, inputProps]);

  useEffect(() => {
    if (getValueAfterInit) {
      const currValue = getValues(name).value;

      if (!currValue && setValue) {
        const valueInit = getValueAfterInit();
        if (valueInit) {
          setTimeout(() => {
            setValue(name, valueInit);
            // setFieldTouched(name as never, true, true);
          }, 1);
        }
      }
    }
  }, [name, setValue, getValues, getValueAfterInit]);

  return (
    <FormControl
      fullWidth
      disabled={disabled}
      error={Boolean(touched && error)}
      className={className}
      classes={{
        root: classes.root,
      }}
    >
      {label && (
        <InputLabel
          htmlFor={inputId}
          classes={{
            root: classes.labelRoot,
            focused: classes.focused,
          }}
          {...labelProps}
        >
          <>
            {label}
            {tooltip && (
              <CustomActionCell
                title={<QuestionCircleIcon className={`${classes.iconTooltip}`} />}
                classNameButtonWrapper={classes.commentButtonWrapper}
                onMouse
              >
                <p className={classes.comment}>
                  {tooltip}
                </p>
              </CustomActionCell>
            )}
          </>
        </InputLabel>
      )}
      <Controller
        name={name}
        control={control}
        render={(props) => (
          <Input
            {...props}
            onChange={(e) => onChangeHandler(e)}
            id={inputId}
            disabled={disabled}
            aria-describedby={errorId}
            endAdornment={endAdornment}
            {...extras}
            {...muiInputProps}
            inputProps={{
              ...defaultInputProps,
              ...inputProps,
            }}
            onBlur={submitOnBlur ? () => { props.onBlur(); submitForm(); } : () => props.onBlur()}
            classes={{
              input: classes.input,
              underline: classes.inputUnderline,
              focused: classes.focused,
              disabled: classes.disabled,
            }}
          />
        )}

      />
      {touched && error && !hideErrors && (
        <FormHelperText id={errorId}>{error}</FormHelperText>
      )}
    </FormControl>
  );
};

export default FormInput;
